import { useEffect } from 'react';
import { site } from '../config/site';
import { getRouteMeta } from '../config/routesMeta';

function setMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const DEFAULT_IMAGE = '/assets/images/imageedit-21-8338150016-892x474.png';

/**
 * Document head manager for the SPA. Values are resolved from the central
 * routesMeta registry by `path`, with explicit props taking precedence. This
 * keeps the runtime head in sync with the static prerender, which reads the
 * same registry at build time.
 */
export default function Seo({
  title,
  description,
  path = '/',
  image,
  jsonLd,
  ogType = 'website',
  noindex = false,
}) {
  const meta = getRouteMeta(path) || {};
  const resolvedTitle = title ?? meta.title;
  const resolvedDescription = description ?? meta.description ?? site.description;
  const resolvedImage = image ?? meta.image ?? DEFAULT_IMAGE;
  const resolvedJsonLd = jsonLd ?? meta.jsonLd;
  const resolvedNoindex = noindex || meta.noindex || false;

  const fullTitle = resolvedTitle
    ? `${resolvedTitle} | ${site.name}`
    : `${site.name} — ${site.tagline}`;
  const canonical = `${site.url}${path}`;
  const absImage = resolvedImage?.startsWith('http')
    ? resolvedImage
    : `${site.url}${resolvedImage}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('name', 'description', resolvedDescription);
    setMeta(
      'name',
      'robots',
      resolvedNoindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    );
    setLink('canonical', canonical);

    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:site_name', site.name);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', resolvedDescription);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', absImage);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', resolvedDescription);
    setMeta('name', 'twitter:image', absImage);

    const id = 'seo-jsonld';
    document.getElementById(id)?.remove();
    if (resolvedJsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = JSON.stringify(resolvedJsonLd);
      document.head.appendChild(script);
    }
  }, [fullTitle, resolvedDescription, canonical, absImage, resolvedJsonLd, resolvedNoindex, ogType]);

  return null;
}
